import { createLatestRequestGuard } from "@/lib/latest-request";
import {
	applyCandidate,
	applyCommonPrefix,
	commonPrefix,
	detectCompletionKind,
	extractCompletionTarget,
	formatCompletionMenuLines,
	unescapeCompletionWord,
	type CompletionKind,
	type CompletionTarget,
} from "@/lib/terminal-completion";
import { TerminalInputParser, type TerminalInputToken } from "@/lib/terminal-input-parser";
import {
	deleteBackward,
	deleteForward,
	deleteWordBefore,
	computeLineLayout,
	insertText,
	killToLineEnd,
	killToLineStart,
	moveLeft,
	moveLineEnd,
	moveLineStart,
	moveRight,
	moveWordLeft,
	moveWordRight,
	renderLineFrame,
	type LineBufferState,
	type LineEditorPrompt,
	type LineFrameState,
} from "@/lib/terminal-line-editor";

export interface LineEditorTerminal {
	readonly cols: number;
	write(data: string): void;
	clear(): void;
}

export type XtermLineEditorHistoryDirection = "previous" | "next";

export interface XtermLineEditorCallbacks {
	onSubmit(text: string): void;
	onHistory(direction: XtermLineEditorHistoryDirection, currentText: string): string | null;
	onBufferChanged(text: string): void;
	onBufferDiscarded(): void;
	onScreenCleared(): void;
	/** 補完候補を取得する。取得できなかった場合は null。 */
	onCompletionRequest(word: string, kind: CompletionKind): Promise<string[] | null>;
}

/** 表示中の補完候補一覧。index が -1 のときは一覧を出しただけで未選択。 */
interface CompletionMenu {
	candidates: string[];
	index: number;
	/** 置換対象スパンより前の文字列。 */
	head: string;
	/** カーソル以降に保持する文字列。 */
	tail: string;
}

const EMPTY_BUFFER: LineBufferState = { text: "", cursor: 0 };
const BELL = "\u0007";

/** xterm の入出力を、1行単位で実行する編集バッファとして管理する。 */
export class XtermLineEditor {
	private readonly parser = new TerminalInputParser();
	private buffer: LineBufferState = EMPTY_BUFFER;
	private prompt: LineEditorPrompt | null = null;
	private frame: LineFrameState | null = null;
	private enabled = true;
	private editing = false;
	private menu: CompletionMenu | null = null;
	private completionPending = false;
	private readonly completionGuard = createLatestRequestGuard();

	constructor(
		private readonly terminal: LineEditorTerminal,
		private readonly callbacks: XtermLineEditorCallbacks,
	) {}

	feed(data: string): void {
		if (!this.enabled) return;

		for (const token of this.parser.feed(data)) {
			if (!this.editing) continue;
			this.applyToken(token);
		}
	}

	beginLine(prompt: LineEditorPrompt): void {
		this.parser.reset();
		this.resetCompletion();
		this.prompt = prompt;
		this.buffer = EMPTY_BUFFER;
		this.frame = null;
		this.editing = true;
		this.render();
	}

	updatePrompt(prompt: LineEditorPrompt): void {
		this.prompt = prompt;
		if (this.editing) this.render();
	}

	replaceBuffer(text: string): void {
		if (!this.editing) return;
		this.buffer = { text, cursor: text.length };
		this.render();
	}

	finalizeLine(): void {
		if (!this.editing) return;
		this.resetCompletion();
		this.buffer = { ...this.buffer, cursor: this.buffer.text.length };
		this.render();
		if (!this.frame?.layout.needsForcedWrap) this.terminal.write("\r\n");
		this.frame = null;
		this.editing = false;
	}

	setEnabled(enabled: boolean): void {
		this.enabled = enabled;
		if (!enabled) {
			this.parser.reset();
			this.resetCompletion();
		}
	}

	handleResize(): void {
		if (!this.editing || this.prompt === null) return;
		if (this.frame !== null) {
			const displayText = this.displayText();
			const cols = Math.max(1, this.terminal.cols);
			const previousForcedWrap = this.frame.layout.needsForcedWrap;
			const layout = computeLineLayout(this.prompt.text, displayText, this.buffer.cursor, cols);
			this.frame = {
				prompt: this.prompt,
				displayText,
				cursor: this.buffer.cursor,
				cols,
				layout: previousForcedWrap
					? {
							...layout,
							// forced wrapのLFはhard breakとしてreflowされるため、
							// リサイズ後も実カーソルは入力末尾の次行にいる。
							cursorRow: layout.endRow + (layout.endCol === 0 ? 0 : 1),
						}
					: layout,
			};
		}
		this.render();
	}

	getBuffer(): string {
		return this.buffer.text;
	}

	private applyToken(token: TerminalInputToken): void {
		if (token.type === "complete") {
			this.completeWord();
			return;
		}
		// moveCursor が早期 return しても footer が残らないよう、ここで確実に消す。
		this.closeMenu();

		switch (token.type) {
			case "insert":
				this.updateBuffer(insertText(this.buffer, token.text));
				return;
			case "backspace":
				this.updateBuffer(deleteBackward(this.buffer));
				return;
			case "delete":
				this.updateBuffer(deleteForward(this.buffer));
				return;
			case "cursorLeft":
				this.moveCursor(moveLeft(this.buffer));
				return;
			case "cursorRight":
				this.moveCursor(moveRight(this.buffer));
				return;
			case "cursorWordLeft":
				this.moveCursor(moveWordLeft(this.buffer));
				return;
			case "cursorWordRight":
				this.moveCursor(moveWordRight(this.buffer));
				return;
			case "lineStart":
				this.moveCursor(moveLineStart(this.buffer));
				return;
			case "lineEnd":
				this.moveCursor(moveLineEnd(this.buffer));
				return;
			case "killToStart":
				this.updateBuffer(killToLineStart(this.buffer));
				return;
			case "killToEnd":
				this.updateBuffer(killToLineEnd(this.buffer));
				return;
			case "deleteWordBefore":
				this.updateBuffer(deleteWordBefore(this.buffer));
				return;
			case "historyPrevious":
				this.recallHistory("previous");
				return;
			case "historyNext":
				this.recallHistory("next");
				return;
			case "enter":
				this.submitOrStartEmptyLine();
				return;
			case "interrupt":
				this.discardLine();
				return;
			case "clearScreen":
				this.terminal.clear();
				this.callbacks.onScreenCleared();
				this.frame = null;
				this.render();
				return;
		}
	}

	private updateBuffer(next: LineBufferState): void {
		const textChanged = next.text !== this.buffer.text;
		this.buffer = next;
		this.render();
		if (textChanged) this.callbacks.onBufferChanged(next.text);
	}

	private moveCursor(next: LineBufferState): void {
		if (next.cursor === this.buffer.cursor) return;
		this.buffer = next;
		this.render();
	}

	/** 候補一覧と実行中の補完要求を破棄する (再描画はしない)。 */
	private resetCompletion(): void {
		this.menu = null;
		this.completionPending = false;
		this.completionGuard.begin();
	}

	/** 候補一覧を閉じ、footer を消すために再描画する。 */
	private closeMenu(): void {
		if (this.menu === null) return;
		this.menu = null;
		this.render();
	}

	private completeWord(): void {
		const menu = this.menu;
		if (menu !== null) {
			menu.index = (menu.index + 1) % menu.candidates.length;
			this.updateBuffer(
				applyCandidate(
					menu.head + menu.tail,
					menu.head.length,
					menu.head.length,
					menu.candidates[menu.index],
				),
			);
			return;
		}
		// 連打で要求を重ねない。
		if (this.completionPending) return;

		const target = extractCompletionTarget(this.buffer.text, this.buffer.cursor);
		const kind = detectCompletionKind(this.buffer.text, target);
		// コマンド位置の空語は候補が多すぎるため、要求せず空白を1つ入れる。
		if (kind === "command" && target.word === "") {
			this.updateBuffer(insertText(this.buffer, " "));
			return;
		}
		void this.requestCompletion(target, kind);
	}

	private async requestCompletion(target: CompletionTarget, kind: CompletionKind): Promise<void> {
		this.completionPending = true;
		const requested = this.buffer;
		const isCurrentRequest = this.completionGuard.begin();
		try {
			const candidates = await this.callbacks.onCompletionRequest(
				unescapeCompletionWord(target.word),
				kind,
			);
			if (!isCurrentRequest() || !this.editing) return;
			// 要求中に編集されていたら、ずれた位置へ挿入しないよう破棄する。
			if (this.buffer.text !== requested.text || this.buffer.cursor !== requested.cursor) return;
			this.applyCompletion(target, candidates);
		} finally {
			this.completionPending = false;
		}
	}

	private applyCompletion(target: CompletionTarget, candidates: string[] | null): void {
		if (candidates === null || candidates.length === 0) {
			this.terminal.write(BELL);
			return;
		}

		const head = this.buffer.text.slice(0, target.start);
		const tail = this.buffer.text.slice(this.buffer.cursor);
		if (candidates.length === 1) {
			this.updateBuffer(applyCandidate(head + tail, head.length, head.length, candidates[0]));
			return;
		}

		// 一覧を出して共通プレフィックスまで補完し、以降の Tab で候補を巡回する。
		this.menu = { candidates, index: -1, head, tail };
		this.updateBuffer(
			applyCommonPrefix(head + tail, head.length, head.length, commonPrefix(candidates)),
		);
	}

	private recallHistory(direction: XtermLineEditorHistoryDirection): void {
		const recalled = this.callbacks.onHistory(direction, this.buffer.text);
		if (recalled !== null) this.replaceBuffer(recalled);
	}

	private submitOrStartEmptyLine(): void {
		if (this.buffer.text.trim().length > 0) {
			this.callbacks.onSubmit(this.buffer.text);
			return;
		}
		const prompt = this.prompt;
		if (prompt === null) return;
		this.finalizeLine();
		this.callbacks.onBufferDiscarded();
		this.beginLine(prompt);
	}

	private discardLine(): void {
		const prompt = this.prompt;
		if (prompt === null) return;
		this.buffer = { ...this.buffer, cursor: this.buffer.text.length };
		this.render();
		this.terminal.write("^C");
		this.terminal.write("\r\n");
		this.frame = null;
		this.editing = false;
		this.callbacks.onBufferDiscarded();
		this.beginLine(prompt);
	}

	private render(): void {
		if (this.prompt === null) return;
		const cols = Math.max(1, this.terminal.cols);
		const menu = this.menu;
		const rendered = renderLineFrame(this.frame, {
			prompt: this.prompt,
			displayText: this.displayText(),
			cursor: this.buffer.cursor,
			cols,
			menuLines: menu === null ? undefined : formatCompletionMenuLines(menu.candidates, cols),
		});
		this.terminal.write(rendered.data);
		this.frame = rendered.frame;
	}

	private displayText(): string {
		return this.buffer.text;
	}
}
